import React, {FC} from 'react';

import {computeIsTicketStatusAllowed} from '../../../../libs/inboxes/business/src/ticketing/ticketStatus';
import {getTags} from '../../../../libs/inboxes/store/src/inboxesSelectors';
import {TicketStatusName} from '../../../../libs/inboxes/ui/src/ticketing/ticketStatusName';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontEvent} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isDefined} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {TicketStatusModel} from '../../../../libs/shared/core/src/models/api/ticketStatusModel';
import {DropdownInheritableProps} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {createSelector} from '../../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {TicketStatusDropdownItemIcon} from './ticketStatusDropdownItemIcon';

/*
 * Props.
 */

interface TicketStatusDropdownProps extends DropdownInheritableProps {
  ticketStatuses: ReadonlyArray<TicketStatusModel>;
  isArchiveAllowed?: boolean;
  selectedTicketStatusTagId: number | undefined;
  onTicketStatusSelect: (event: FrontEvent, ticketStatus: TicketStatusModel) => void;
  openedByKeyboardAction?: boolean;
}

/*
 * Component.
 */

export const TicketStatusDropdown: FC<TicketStatusDropdownProps> = (props) => {
  const {
    ticketStatuses,
    isArchiveAllowed = true,
    selectedTicketStatusTagId,
    onTicketStatusSelect,
    openedByKeyboardAction,
    ...popoverProps
  } = props;

  const selectedTicketStatusTag = useAppSelector((state) => getSelectedTicketStatusTag(state, props));

  const isSelectedTicketStatusArchived = (ticketStatus: TicketStatusModel) =>
    ticketStatus.tagId === selectedTicketStatusTag?.id && isDefined(selectedTicketStatusTag.archivedAt);

  const isTicketStatusAllowed = (ticketStatus: TicketStatusModel) =>
    computeIsTicketStatusAllowed(selectedTicketStatusTagId, ticketStatus, {
      isOnlineArchiveAllowed: isArchiveAllowed,
    });

  const isChecked = (ticketStatus: TicketStatusModel) => ticketStatus.tagId === selectedTicketStatusTagId;

  const defaultHighlightedIndex = ticketStatuses.findIndex((ticketStatus) => isChecked(ticketStatus));

  return (
    <SimpleDropdown
      {...popoverProps}
      defaultHighlightedIndex={
        openedByKeyboardAction && defaultHighlightedIndex >= 0 ? defaultHighlightedIndex : undefined
      }
    >
      {ticketStatuses.map((ticketStatus) => (
        <DropdownItem
          key={ticketStatus.tagId}
          shouldReserveRightIcon
          isDisabled={!isTicketStatusAllowed(ticketStatus) || isSelectedTicketStatusArchived(ticketStatus)}
          isChecked={isChecked(ticketStatus)}
          onSelect={(event) => onTicketStatusSelect(event, ticketStatus)}
          renderTooltip={
            isSelectedTicketStatusArchived(ticketStatus) ? renderArchivedTicketStatusTooltip : undefined
          }
        >
          <TicketStatusDropdownItemIcon category={ticketStatus.category} />
          <DropdownItemContent isChecked={isChecked(ticketStatus)}>
            <TicketStatusName tagId={ticketStatus.tagId} />
          </DropdownItemContent>
        </DropdownItem>
      ))}
    </SimpleDropdown>
  );
};

const renderArchivedTicketStatusTooltip = () => (
  <Trans id="ticket-status-dropdown-item-disabled-archived">
    This ticket status is archived and can not be used.
  </Trans>
);

/*
 * Selectors.
 */

const getSelectedTicketStatusTagId = (_: unknown, params: TicketStatusDropdownProps) =>
  params.selectedTicketStatusTagId;

const getSelectedTicketStatusTag = createSelector([getSelectedTicketStatusTagId, getTags], (tagId, tags) =>
  isDefined(tagId) ? tags[tagId] : undefined,
);
